import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, FileX2, Send, Info } from "lucide-react";

const ArrepentimientoButton = () => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const [form, setForm] = useState({
    nombre: "",
    dni: "",
    email: "",
    telefono: "",
    servicio: "",
    motivo: "",
  });

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.nombre || !form.dni || !form.email || !form.servicio) {
      toast({
        title: "Completá los datos obligatorios",
        description: "Nombre, DNI, email y servicio son requeridos.",
        variant: "destructive",
      });
      return;
    }
    const text = encodeURIComponent(
      `BOTÓN DE ARREPENTIMIENTO — Solicito la baja/arrepentimiento del servicio dentro del plazo legal de 10 días (Res. 424/2020).\n\nNombre: ${form.nombre}\nDNI: ${form.dni}\nEmail: ${form.email}\nTeléfono: ${form.telefono}\nServicio: ${form.servicio}\n\nMotivo: ${form.motivo || "No especificado"}`,
    );
    const mailto = `mailto:administracion@silicom.com.ar?subject=${encodeURIComponent("Botón de Arrepentimiento - Solicitud de baja")}&body=${text}`;
    window.location.href = mailto;
    window.open(`https://wa.me/5491127790411?text=${text}`, "_blank");
    toast({
      title: "Solicitud registrada",
      description:
        "Tu solicitud fue enviada por email y WhatsApp. Recibirás confirmación en las próximas horas.",
    });
    setOpen(false);
  };

  return (
    <>
      <button
        data-testid="arrepentimiento-btn"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-full border-2 border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-rose-600 transition-colors hover:border-rose-300 hover:bg-rose-100"
      >
        <FileX2 className="h-4 w-4" />
        Botón de Arrepentimiento
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="arrepentimiento-modal"
          className="max-h-[92vh] max-w-2xl overflow-y-auto border-rose-200 bg-white text-slate-900"
        >
          <DialogHeader>
            <div className="mb-2 inline-flex items-center gap-2 self-start rounded-full bg-rose-50 px-3 py-1 text-xs font-bold uppercase tracking-widest text-rose-600">
              <AlertTriangle className="h-3.5 w-3.5" />
              Resolución 424/2020
            </div>
            <DialogTitle className="text-2xl font-black text-slate-900 md:text-3xl">
              Botón de Arrepentimiento
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Podés solicitar la baja o arrepentirte del servicio{" "}
              <b className="text-slate-900">dentro de los 10 días corridos</b>{" "}
              desde la contratación o instalación, sin costo alguno, según la
              Ley de Defensa del Consumidor Nº 24.240 y la Resolución 424/2020
              de la Secretaría de Comercio Interior.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 flex items-start gap-2 rounded-xl border border-sky-100 bg-sky-50 p-3 text-xs text-slate-700">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-500" />
            <p>
              Al enviar el formulario recibiremos tu solicitud y nos pondremos
              en contacto en un plazo máximo de 24 hs hábiles para confirmar la
              baja.
            </p>
          </div>

          <form
            onSubmit={submit}
            data-testid="arrepentimiento-form"
            className="mt-4 grid gap-3 sm:grid-cols-2"
          >
            <div>
              <Label className="text-slate-700">Nombre y Apellido *</Label>
              <Input
                data-testid="arr-nombre"
                value={form.nombre}
                onChange={onChange("nombre")}
                className="mt-1 border-slate-200 bg-white text-slate-900"
              />
            </div>
            <div>
              <Label className="text-slate-700">DNI *</Label>
              <Input
                data-testid="arr-dni"
                value={form.dni}
                onChange={onChange("dni")}
                className="mt-1 border-slate-200 bg-white text-slate-900"
              />
            </div>
            <div>
              <Label className="text-slate-700">Email *</Label>
              <Input
                data-testid="arr-email"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                className="mt-1 border-slate-200 bg-white text-slate-900"
              />
            </div>
            <div>
              <Label className="text-slate-700">Teléfono</Label>
              <Input
                data-testid="arr-tel"
                value={form.telefono}
                onChange={onChange("telefono")}
                className="mt-1 border-slate-200 bg-white text-slate-900"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-slate-700">Servicio contratado *</Label>
              <Input
                data-testid="arr-servicio"
                value={form.servicio}
                onChange={onChange("servicio")}
                placeholder="Ej: Hogar 300 Mbps, Hogar Pro 500 Mbps"
                className="mt-1 border-slate-200 bg-white text-slate-900"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-slate-700">Motivo (opcional)</Label>
              <Textarea
                data-testid="arr-motivo"
                value={form.motivo}
                onChange={onChange("motivo")}
                rows={3}
                className="mt-1 border-slate-200 bg-white text-slate-900"
              />
            </div>
          </form>

          <DialogFooter className="mt-4 flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            >
              Cancelar
            </Button>
            <Button
              data-testid="arr-submit"
              onClick={submit}
              className="bg-rose-500 font-bold text-white hover:bg-rose-600"
            >
              <Send className="mr-2 h-4 w-4" /> Enviar solicitud de baja
            </Button>
          </DialogFooter>

          <div className="mt-2 rounded-lg bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-600">
            También podés solicitar la baja personalmente en{" "}
            <b className="text-slate-900">Leandro N. Alem 734, Merlo</b>, por
            email a{" "}
            <a
              href="mailto:administracion@silicom.com.ar"
              className="text-sky-600 hover:underline"
            >
              administracion@silicom.com.ar
            </a>{" "}
            o llamando al <b className="text-slate-900">0810-345-1201</b>.
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ArrepentimientoButton;
